import * as React from "react";
import {
  Card,
  CardHeader,
  Body1,
  Caption1,
  Button,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Dropdown,
  Option,
  Textarea,
  Label,
  Link,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { AlertEntry } from "../../services/workbookAnalyticsRulesEngine";
import { navigateToAlert } from "../../services/excelNavigation";

const useStyles = makeStyles({
  card: {
    marginBottom: "12px",
    width: "100%",
  },
  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  headerContent: {
    flex: 1,
  },
  message: {
    marginTop: "8px",
    marginBottom: "12px",
  },
  timestamp: {
    color: tokens.colorNeutralForeground3,
  },
  cardFooter: {
    display: "flex",
    justifyContent: "flex-end",
    paddingTop: "8px",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  dismissButton: {
    minWidth: "auto",
  },
  dialogField: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    marginBottom: "16px",
  },
});

const DISMISS_REASONS = [ //TODO: reasons
  { key: "outdated_source_document", text: "Outdated source document" },
  { key: "incorred_rule_logic", text: "Rule logic is incorrect" },
  { key: "system_parsing_error", text: "System has read values incorrectly" },
  { key: "rule_not_applicable", text: "Rule should not apply, (please specify why)", requiresReason: true },
  { key: "other", text: "Other reason (please specify)", requiresReason: true },
];

interface AlertCardProps {
  alert: AlertEntry;
  onDismiss?: (alertId: string, reasonCode: string, description?: string) => void;
}

const AlertCard: React.FC<AlertCardProps> = ({ alert, onDismiss }) => {
  const styles = useStyles();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [selectedReason, setSelectedReason] = React.useState<string>("");
  const [otherDescription, setOtherDescription] = React.useState("");

  const formattedTime = new Date(alert.timestamp).toLocaleTimeString();
  const formattedDate = new Date(alert.timestamp).toLocaleDateString();

  const handleDismissClick = () => {
    if (!onDismiss || !selectedReason) return;
    
    const description = otherDescription.trim() || undefined;
    onDismiss(alert.id, selectedReason, description);
    setDialogOpen(false);
    setSelectedReason("");
    setOtherDescription("");
  };

  const selectedReasonObj = DISMISS_REASONS.find(r => r.key === selectedReason);
  const requiresReason = selectedReasonObj?.requiresReason || false;
  const canSubmit = selectedReason && (!requiresReason || otherDescription.trim());

  return (
    <Card className={styles.card}>
      <CardHeader
        header={
          <div className={styles.headerContent}>
            <Body1 title={`First raised: ${formattedDate} ${formattedTime} (CODE: ${alert.code})`}><strong>{alert.info.title}</strong></Body1>
            {/* <Caption1 className={styles.timestamp}>
              {formattedDate} {formattedTime}
            </Caption1> */}
          </div>
        }
      />
      <Body1 className={styles.message}>{alert.info.message}</Body1>
      <Caption1>
        <Link onClick={() => navigateToAlert(alert)}>show</Link>
      </Caption1>
      {onDismiss && (
        <div className={styles.cardFooter}>
          <Dialog open={dialogOpen} onOpenChange={(_, data) => setDialogOpen(data.open)}>
            <DialogTrigger disableButtonEnhancement>
              <Button
                appearance="subtle"
                size="small"
                className={styles.dismissButton}
              >
                Dismiss
              </Button>
            </DialogTrigger>
            <DialogSurface>
              <DialogBody>
                <DialogTitle>Dismiss Alert</DialogTitle>
                <DialogContent>
                  <div className={styles.dialogField}>
                    <Label htmlFor="dismiss-reason">Why are you dismissing this alert?</Label>
                    <Dropdown
                      id="dismiss-reason"
                      placeholder="Select a reason"
                      value={DISMISS_REASONS.find(r => r.key === selectedReason)?.text || ""}
                      onOptionSelect={(_, data) => setSelectedReason(data.optionValue as string)}
                    >
                      {DISMISS_REASONS.map((reason) => (
                        <Option key={reason.key} value={reason.key}>
                          {reason.text}
                        </Option>
                      ))}
                    </Dropdown>
                  </div>
                  {selectedReason && (
                    <div className={styles.dialogField}>
                      <Label htmlFor="other-description">
                        {requiresReason ? "Please provide more details (required)" : "Additional details (optional)"}
                      </Label>
                      <Textarea
                        id="other-description"
                        value={otherDescription}
                        onChange={(_, data) => setOtherDescription(data.value)}
                        placeholder="Describe your reason..."
                        rows={3}
                      />
                    </div>
                  )}
                </DialogContent>
                <DialogActions>
                  <DialogTrigger disableButtonEnhancement>
                    <Button appearance="secondary">Cancel</Button>
                  </DialogTrigger>
                  <Button 
                    appearance="primary" 
                    onClick={handleDismissClick}
                    disabled={!canSubmit}
                  >
                    Dismiss
                  </Button>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>
        </div>
      )}
    </Card>
  );
};

export default AlertCard;
